import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import useAppointment from '../../../../../hooks/useAppointment';
import { useBooth } from '../../../../../hooks/useBooth';
import { usePermission } from '../../../../../hooks/usePermission';
import { Booth, Meetingroom, PDFFile } from '../../../../../interfaces/interfaces';
import { setTeamUsers } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { getAllUsers } from '../../../../../services/services';
import { AppointmentCreateModal } from '../../../../account/Appointment/AppointmentCreateModal';
import BoothHeaderImages from '../../../../account/Booth/BoothInfo/BoothHeaderImages/BoothHeaderImages';
import BoothPDFs from '../../../../account/Booth/BoothInfo/BoothPDFs';
import BoothVideoAndRichtext from '../../../../account/Booth/BoothInfo/BoothVideoAndRichtext';
import VyButton from '../../../../common/VyButton';
import InlineEditButton from '../../../pageBuilder/InlineEditButton';
import AppointmentBoard from '../../VystemLight/appointment/AppointmentBoard';
import NetworkRoom from '../../VystemLight/network/NetworkRoom';
import AddRoomButton from '../../VystemLight/oneonone/BigBooth/AddRoomButton';
import HeaderEdit from '../../VystemLight/oneonone/BigBooth/HeaderEdit';
import Section from '../../VystemLight/oneonone/BigBoothTwoColsSection';
import BoothLinkedPersonEdit from '../../VystemLight/oneonone/BoothLinkedPersonEdit';
import ChildBooth from '../../VystemLight/oneonone/ChildBooth';
import MeetingRoomTemplateEdit from '../../VystemLight/oneonone/MeetingRoomTemplateEdit';
import NewBoothLinkedPerson from '../../VystemLight/oneonone/NewBoothLinkedPerson';
import Link from 'next/link';

export interface Section {
  title: string;
  meetingrooms: Meetingroom[] | undefined;
  type: 'meeting' | 'conference';
  limit: number;
  onAdd: () => Promise<void>;
}

interface BoothEditInterface {
  booth: Booth;
  filters?: any;
  isEditMode: boolean;
  setIsOpen: (value: boolean) => void;
  setViewerOpen: (value: boolean) => void;
  setIsEditMode: (value: boolean) => void;
}
const BoothEdit = ({ booth, isEditMode, setIsOpen, setIsEditMode }: BoothEditInterface) => {
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [sections, setSections] = useState<Section[]>([]);

  const event = useSelector((state) => state.common.event);
  const networks = useSelector((state) => state.common.networks);
  const user = useSelector((state) => state.common.user);
  const orgMembers = useSelector(({ common }) => common.teamUsers);

  const { t } = useTranslation(['booth']);

  const { isBoothHost } = usePermission();
  const { updateProp, createNewMeetingroom, createNewConferenceRoom, fetchAndUpdateBooth, deleteLinkedPerson } = useBooth();
  const dispatch = useDispatch();
  const { initializeAppointments } = useAppointment(booth._id);

  useEffect(() => {
    if (!event) return;
    const meetingTypeMeetingrooms = booth?.meetingrooms ?? [];
    const meetingTypeConferencerooms = booth?.conferencerooms ?? [];
    const _sections: Section[] = [
      {
        title: event?.template.individualNames.meetingrooms || t('booth:chatrooms'),
        meetingrooms: meetingTypeMeetingrooms,
        type: 'meeting',
        limit: booth.maxMeetings,
        onAdd: async () => await createNewMeetingroom(booth._id, () => fetchAndUpdateBooth(booth._id)),
      },
      {
        title: event?.template.individualNames.conferencerooms || t('booth:conferencerooms'),
        meetingrooms: meetingTypeConferencerooms,
        type: 'conference',
        limit: booth.maxConferences,
        onAdd: async () => await createNewConferenceRoom(booth._id, () => fetchAndUpdateBooth(booth._id)),
      },
    ];
    setSections(_sections);
  }, [event, booth]);

  const fetchMembers = async () => {
    if (orgMembers.length > 0) return;
    try {
      const { data } = await getAllUsers(50);
      dispatch(setTeamUsers([{ ...user!, organization: user!.organization!._id }, ...data.filter((m) => m._id !== user!._id)]));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const boothNetworks = networks.filter((network) => network?.booth?._id === booth._id);
  return (
    <div className="modal font-xs px-4 py-8 max-h-[80vh] overflow-y-auto">
      <div className="flex text-[#87a8d8] text-base">
        <div className="sticky top-0 z-30 py-2 w-full bg-white shadow-sm">
          <HeaderEdit booth={booth} update={async (path, value) => await updateProp(booth._id, path, value)} hideContactShareButton hideLogo />
        </div>
        {isBoothHost(user, [booth]) ? (
          <InlineEditButton isEditMode={isEditMode} setIsEditMode={setIsEditMode} forceShow tooltip={t('pagebuilder:booths-edit-details')} />
        ) : (
          <FontAwesomeIcon style={{ color: 'black', cursor: 'pointer', marginLeft: 'auto' }} icon={faTimes} onClick={() => setIsOpen(false)} />
        )}
      </div>

      <p className="w-full text-3xl text-left text-[#1a3363] p-1 hyphens-manual border-b-2" dangerouslySetInnerHTML={{ __html: booth?.slogan ?? '' }} />
      {booth?.boothVideos && booth?.headerImages && booth?.headerImages?.length > 0 && (
        <div className="px-8 mx-4 border-b-2">
          <BoothHeaderImages booth={booth} />
        </div>
      )}

      {booth.description && (
        <div className="w-full px-4 text-[#1a3363] text-sm border-b-2">
          <BoothVideoAndRichtext booth={booth} onRefresh={() => fetchAndUpdateBooth(booth._id)} isInline hideBoothWebsite />
        </div>
      )}

      {booth.childBooths && booth.childBooths.length > 0 && (
        <Section title={event?.template.individualNames.subBooths || ''}>
          {booth.childBooths.map((booth) => (
            <ChildBooth key={booth._id} booth={booth} />
          ))}
        </Section>
      )}
      {event?.settings?.isLinkedBoothMembersActive && (
        <div className="w-full px-4 text-[#1a3363] text-sm border-b-2 pb-4">
          <h1 className="mt-4 text-xl font-bold">{event?.template.individualNames.linkedPeople ?? t('booth:linked-people')}</h1>
          <Section>
            {booth.linkedPeople &&
              booth.linkedPeople
                .map((person) => ({ ...person, avatar: person.avatar ?? person.user.avatar, name: person.name ?? person.user.name }))
                .map((person) => (
                  <BoothLinkedPersonEdit
                    key={person.user._id}
                    person={person}
                    booth={booth}
                    onLinkedPersonDelete={() => deleteLinkedPerson(booth, person.user._id)}
                  />
                ))}
            {booth.linkedPeople && booth.linkedPeople.length < orgMembers.length && <NewBoothLinkedPerson booth={booth} />}
          </Section>
        </div>
      )}

      {sections.map((section, i) => {
        return (
          <div className="w-full px-4 text-[#1a3363] text-sm border-b-2" key={`rooms-section-${i}`}>
            {section && (
              <>
                <Section twoCols={false} title={section.title}>
                  {section?.meetingrooms?.map((meetingroom) => (
                    <MeetingRoomTemplateEdit key={meetingroom._id} meetingroom={meetingroom} booth={booth} hideJoinButton />
                  ))}
                </Section>
                <AddRoomButton booth={booth} onAdd={section.onAdd} type={section.type} limit={section.limit} sectionValue={section.meetingrooms?.length ?? 0} />
              </>
            )}
          </div>
        );
      })}
      {event?.modules?.booking?.active && booth?.settings?.enableAppointments.value && (
        <>
          <h1 className="mt-12 mb-4 text-xl font-bold text-gray-700">{event.template.individualNames.appointments}</h1>
          {booth && booth?.meetingrooms?.length > 0 && (
            <VyButton isAddButton className="mb-4" onClick={() => setShowAddAppointment(true)}>
              {t('appointment:add-appointment-slot')}
            </VyButton>
          )}
          <div className="overflow-auto pt-2 pb-4 w-full">
            <AppointmentBoard
              event={event}
              date={event.datetime_start}
              endDate={event.datetime_end}
              meetingrooms={booth?.meetingrooms}
              bookingmode
              boothId={booth._id}
            />
          </div>
          {showAddAppointment && booth && booth?.meetingrooms && (
            <AppointmentCreateModal
              booth={booth}
              onCreate={() => {
                initializeAppointments(true);
                setShowAddAppointment(false);
              }}
              open={showAddAppointment}
              onClose={() => setShowAddAppointment(false)}
            />
          )}
        </>
      )}

      {booth?.settings?.displayNetworkingroomsInBooth?.value && boothNetworks.length > 0 && (
        <>
          <h1 className="mt-4 mb-4 text-xl font-bold">{t('booth:networking-rooms')}</h1>
          {boothNetworks.map((network) => (
            <div className="flex overflow-auto justify-center pt-2 pb-4 w-full">
              <NetworkRoom key={network._id} network={network} />
            </div>
          ))}
        </>
      )}
      <div className="flex flex-col justify-start px-4 m-auto w-full text-center">
        <div className="w-full px-4 text-[#1a3363] text-sm border-b-2">
          <BoothVideoAndRichtext booth={booth} onRefresh={() => fetchAndUpdateBooth(booth._id)} isInline hideBoothVideos hideBoothDescription />
        </div>

        {booth.pdfUploads?.map((pdf: PDFFile, idx: any, array: any) => (
          <Link key={pdf._id} className="action-button" href={createDisplayLink(pdf.file)} target="_blank">
            {array.length < 2 ? 'Weitere Infos (PDF)' : pdf.name}
          </Link>
        ))}
        <div className="my-4">
          <BoothPDFs booth={booth} isInline onRefresh={() => fetchAndUpdateBooth(booth._id)} />
        </div>
      </div>
    </div>
  );
};
export default BoothEdit;
